import {
  faArrowsLeftRight,
  faChevronLeft,
  faChevronRight,
  faDownload,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
  faRotateLeft,
  faRotateRight,
  faTrash,
  faXmark,
  faDropletSlash,
} from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'
import type React from 'react'
import { useState } from 'react'
import styled, { keyframes } from 'styled-components'

import useUser from '@/hooks/useUser'
import type { Document, SupportingDocument } from '@/types/Activity'
import { Role } from '@/types/User'
import { hasRole } from '@/utils/role'
import { useUserTracking } from '@/utils/tracking'

import ControlButton from './ControlButton'
import DeleteDocument from './DeleteDocument'
import DocumentMetaData from './DocumentMetaData'
import PreviewDocument from './PreviewDocument'

interface Props {
  images: Document[] | SupportingDocument[]
  onClose: () => void
  startIndex?: number
  customerId: string
  subjectId: string
}

export const slideInTop = keyframes`
  0% {
    -webkit-transform: translateX(1000px);
    transform: translateX(1000px);
  }
  100% {
    -webkit-transform: translateX(0);
    transform: translateX(0);
  }
`

const HeaderControlsContainer = styled.div`
  display: grid;
  grid-gap: 20px;
  grid-template-columns: repeat(4, min-content);
`

const NavigatorContainer = styled.div`
  display: grid;
  grid-template-columns: min-content 1fr min-content;
  align-items: center;
  justify-items: center;
`

const Container = styled.div<{ isExpanded?: boolean }>`
  position: fixed;
  height: 100%;
  width: ${props => (props.isExpanded ? '90%' : '40vw')};
  top: 0;
  right: 0;
  z-index: 10000000000000;
  display: grid;
  padding: 10px;
  grid-template-rows: min-content min-content 1fr min-content;
  background: rgb(29, 30, 34);
  color: white;
  grid-gap: 10px;
  animation: ${slideInTop} 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  transition: all 0.2s ease;
`

const PreviewContainer = styled.div`
  display: grid;
  grid-template-rows: 1fr min-content;
  min-height: 0;
`

const ImageContainer = styled.div`
  min-height: 0;
  overflow-y: scroll;
  overflow-x: scroll;
  display: grid;
  justify-content: center;

  &::-webkit-scrollbar {
    height: 20px;
    width: 20px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgb(52, 190, 189);
  }
`

const FooterContainer = styled.div`
  display: grid;
  grid-gap: 5px;
`

const ControlsContainer = styled.div`
  display: grid;
  grid-gap: 20px;
  grid-template-columns: repeat(7, min-content);
  justify-content: center;
`

const wasUploadedByReferencingExec = (document: Document | SupportingDocument): boolean =>
  (document as Document)?.uploadedBy?.type === 'ReferencingTeam'

const isDocument = (document: Document | SupportingDocument): document is Document =>
  (document as Document).fileName !== undefined

export const getFile = (
  document: Document | SupportingDocument,
  showWatermarkFile: boolean = true,
) => {
  if (isDocument(document)) {
    if (showWatermarkFile && document.watermarkedFileName) {
      return document.watermarkedFileName
    }
    return document.fileName
  }
  return document.documentReference ?? document.thumbnail
}

const Lightbox: React.FC<Props> = ({
  images,
  onClose,
  startIndex = 0,
  customerId,
  subjectId,
}) => {
  const [index, setIndex] = useState(startIndex)
  const [rotation, setRotation] = useState(0)
  const [scale, setScale] = useState(1)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showWatermarkVersion, setShowWatermarkVersion] = useState(true)

  const { trackUserActivity } = useUserTracking()

  const { isDeveloperModeEnabled, roles } = useUser()

  const currentlySelectedDocument = images[index]

  function increaseScale() {
    setScale(scale + 0.3)
  }

  function decreaseScale() {
    if (scale === 0.3) return
    setScale(scale - 0.3)
  }

  function rotateClockwise() {
    setRotation(rotation === 270 ? 0 : rotation + 90)
  }

  function rotateCounterClockwise() {
    setRotation(rotation === 0 ? 270 : rotation - 90)
  }

  function handleImgChange(direction: 'BACK' | 'NEXT' | 'KEY_PRESS', e?: KeyboardEvent) {
    let newIndex = index

    if (direction === 'KEY_PRESS' && e) {
      if (e.key === 'ArrowLeft') {
        direction = 'BACK'
      } else if (e.key === 'ArrowRight') {
        direction = 'NEXT'
      }
    }

    switch (direction) {
      case 'BACK':
        if (index === 0) return
        newIndex = newIndex - 1
        setIndex(newIndex)
        trackUserActivity('View document', {
          subjectId,
          customerId,
          fileName: getFile(images[newIndex], showWatermarkVersion),
        })
        break
      case 'NEXT':
        if (index === images.length - 1) return
        newIndex = newIndex + 1
        setIndex(newIndex)
        trackUserActivity('View document', {
          subjectId,
          customerId,
          fileName: getFile(images[newIndex], showWatermarkVersion),
        })
    }
  }

  if (!images || images.length === 0) {
    return (
      <Container>
        <h1>No images in gallery!</h1>
      </Container>
    )
  }

  const isSupervisorOrAdmin = hasRole(roles, [Role.supervisor, Role.admin])

  const canDeleteDocument =
    isDocument(currentlySelectedDocument) &&
    currentlySelectedDocument.id !== undefined &&
    (wasUploadedByReferencingExec(currentlySelectedDocument) || isSupervisorOrAdmin)

  return (
    <Container isExpanded={isExpanded}>
      <HeaderControlsContainer>
        <ControlButton
          icon={faArrowsLeftRight}
          tooltipMessage={`${isExpanded ? 'Collapse' : 'Expand'} this view`}
          onClick={() => setIsExpanded(!isExpanded)}
        />
        <ControlButton
          icon={faXmark}
          tooltipMessage="Close this view"
          onClick={onClose}
        />
      </HeaderControlsContainer>
      <div>
        <NavigatorContainer>
          <ControlButton
            icon={faChevronLeft}
            tooltipMessage="See previous document"
            onClick={() => {
              handleImgChange('BACK')
              setIsDeleting(false)
            }}
          />
          <div>
            Document {index + 1} of {images.length}
          </div>
          <ControlButton
            icon={faChevronRight}
            tooltipMessage="See next document"
            onClick={() => {
              handleImgChange('NEXT')
              setIsDeleting(false)
            }}
          />
        </NavigatorContainer>
      </div>
      <PreviewContainer>
        <ImageContainer>
          <PreviewDocument
            file={getFile(images[index], showWatermarkVersion)}
            rotation={rotation}
            scale={scale}
          />
        </ImageContainer>
        <div>
          {canDeleteDocument && isDeleting && (
            <DeleteDocument
              customerId={customerId}
              subjectId={subjectId}
              documentId={(currentlySelectedDocument as Document).id as string}
              onCancel={() => setIsDeleting(false)}
            />
          )}
        </div>
      </PreviewContainer>
      <FooterContainer>
        <ControlsContainer>
          <ControlButton
            icon={faRotateLeft}
            tooltipMessage="Rotate left"
            onClick={rotateCounterClockwise}
          />
          <ControlButton
            icon={faRotateRight}
            tooltipMessage="Rotate right"
            onClick={rotateClockwise}
          />
          <ControlButton
            icon={faMagnifyingGlassMinus}
            tooltipMessage="Zoom out"
            onClick={decreaseScale}
          />
          <ControlButton
            icon={faMagnifyingGlassPlus}
            tooltipMessage="Zoom in"
            onClick={increaseScale}
          />
          <ControlButton
            icon={faTrash}
            tooltipMessage={
              canDeleteDocument
                ? 'Delete this document'
                : 'This document cannot be deleted'
            }
            onClick={() => {
              if (!canDeleteDocument) return
              setIsDeleting(true)
            }}
            disabled={!canDeleteDocument}
          />
          <ControlButton
            icon={faDownload}
            tooltipMessage="Download this document"
            onClick={() => {
              const file = getFile(images[index], showWatermarkVersion)
              window.open(file, '_blank', 'noopener')
              trackUserActivity('Download document', {
                subjectId,
                customerId,
                fileName: file,
              })
            }}
          />
          {isDeveloperModeEnabled && (
            <ControlButton
              icon={faDropletSlash}
              tooltipMessage="Toggle watermark"
              onClick={() => {
                const file = getFile(images[index], showWatermarkVersion)

                const toggledVersion = !showWatermarkVersion

                if (!toggledVersion) {
                  trackUserActivity('Watermark hidden', {
                    subjectId,
                    customerId,
                    fileName: file,
                  })
                }
                setShowWatermarkVersion(toggledVersion)
              }}
            />
          )}
        </ControlsContainer>
        <div className="side-by-side-flex-center">
          {isDocument(images[index]) &&
            ((images[index] as Document).uploadedBy?.authorName ? (
              <div>
                Uploaded {moment((images[index] as Document).createdAt).format('LLLL')} by{' '}
                {(images[index] as Document).uploadedBy?.authorName} (
                {(images[index] as Document).uploadedBy?.authorEmail})
              </div>
            ) : (
              <div>
                Uploaded {moment((images[index] as Document).createdAt).format('LLLL')} by{' '}
                {(images[index] as Document).uploadedBy?.type}
              </div>
            ))}
        </div>
        <DocumentMetaData properties={(images[index] as Document).properties} />
      </FooterContainer>
    </Container>
  )
}

export default Lightbox
